import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import {
  pageSectionHeaderSx,
  pageSectionSubtitleSx,
  pageSectionTitleSx,
} from "../../constants/pageSectionStyles";
import {
  regulationDetailsSectionTabStripSx,
  regulationDetailsSectionTabsSx,
} from "../../constants/regulationDetailsNavTabStyles";
import { useAcTenantId } from "../../hooks/useAcTenantId";
import {
  useHasPrivilege,
  useHasPrivilegeStatus,
} from "../../hooks/useHasPrivilege";
import { useModuleDisplayName } from "../../hooks/useModuleDisplayName";
import {
  AI_HUB_PAGE_READ_PRIVILEGES,
  IMPLEMENTATION_RECORD_PRIVILEGES,
  OBJECT_TO_OBJECT_ASSESSMENT_PRIVILEGES,
  TAGGING_RESULT_PRIVILEGES,
} from "../../services/permissionsService";
import { useRegulationsNextShellIntegration } from "../shared/useRegulationsNextShellIntegration";
import {
  AI_HUB_TAB_LABELS,
  isAiHubTabKey,
  type AiHubTabKey,
} from "./constants";
import { useAiHubListFiltersStore } from "./aiHubListFiltersStore";
import TaggingResultTab from "./TaggingResultTab";
import ConditionalTaggingResultTab from "./ConditionalTaggingResultTab";
import ObjectAnalysisTab from "./ObjectAnalysis/ObjectAnalysisTab";
import ImplementationRecordTab from "./ImplementationRecords/ImplementationRecordTab";
import TriageResultTab from "./TriageResult/TriageResultTab";

/** Temporarily hide Triage Results until the feature is released. */
const SHOW_TRIAGE_RESULT_TAB = false;

export default function AiHubPage() {
  useRegulationsNextShellIntegration();
  const location = useLocation();
  const acTenantId = useAcTenantId();
  const moduleDisplayName = useModuleDisplayName();
  const { has: canOpenAiHubPage, loaded: privilegesLoaded } =
    useHasPrivilegeStatus([...AI_HUB_PAGE_READ_PRIVILEGES], "any");
  const tabPrivilegeOptions = { includeAdmin: false } as const;
  const canReadTaggingResult = useHasPrivilege(
    TAGGING_RESULT_PRIVILEGES.read,
    "any",
    tabPrivilegeOptions,
  );
  const canReadObjectAssessment = useHasPrivilege(
    OBJECT_TO_OBJECT_ASSESSMENT_PRIVILEGES.read,
    "any",
    tabPrivilegeOptions,
  );
  const canReadImplementationRecords = useHasPrivilege(
    IMPLEMENTATION_RECORD_PRIVILEGES.read,
    "any",
    tabPrivilegeOptions,
  );
  const requestedTab = (location.state as { tab?: string } | null)?.tab;
  const tab = useAiHubListFiltersStore((state) => state.activeTab);
  const setActiveTab = useAiHubListFiltersStore((state) => state.setActiveTab);
  const tenantRemountKey = acTenantId || "no-ac-tenant";

  const visibleTabs = useMemo(() => {
    const tabs: AiHubTabKey[] = [];
    if (canReadTaggingResult) {
      tabs.push("taggingResult");
      tabs.push("conditionalTaggingResult");
    }
    if (canReadObjectAssessment) {
      tabs.push("objectToObjectAnalysisResults");
    }
    if (canReadImplementationRecords) {
      tabs.push("implementationRecords");
    }
    if (SHOW_TRIAGE_RESULT_TAB && canReadTaggingResult) {
      tabs.push("triageResult");
    }
    return tabs;
  }, [canReadImplementationRecords, canReadObjectAssessment, canReadTaggingResult]);

  const showAccessDenied = privilegesLoaded && !canOpenAiHubPage;

  useEffect(() => {
    if (isAiHubTabKey(requestedTab) && visibleTabs.includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab, setActiveTab, visibleTabs]);

  const activeTab = visibleTabs.includes(tab) ? tab : visibleTabs[0];

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, value: AiHubTabKey) => {
      setActiveTab(value);
    },
    [setActiveTab],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Box sx={{ ...pageSectionHeaderSx, pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            pb: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...pageSectionTitleSx, mb: "4px" }}>
              {moduleDisplayName}
            </Typography>
            <Typography sx={pageSectionSubtitleSx}>
              Centralize assessment results in one place for efficient review
              and decision-making.
            </Typography>
          </Box>
        </Box>

        {!showAccessDenied && visibleTabs.length > 0 ? (
          <Box sx={regulationDetailsSectionTabStripSx}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={regulationDetailsSectionTabsSx}
            >
              {visibleTabs.map((tabKey) => (
                <Tab
                  key={tabKey}
                  value={tabKey}
                  label={AI_HUB_TAB_LABELS[tabKey]}
                />
              ))}
            </Tabs>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          bgcolor: "#f9fafb",
          px: "24px",
          py: "16px",
        }}
      >
        {showAccessDenied ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              gap: 1,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Access Denied
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`You do not have permission to view ${moduleDisplayName} results.`}
            </Typography>
          </Box>
        ) : (
          <>
            {activeTab === "taggingResult" ? (
              <TaggingResultTab key={tenantRemountKey} />
            ) : null}
            {activeTab === "conditionalTaggingResult" ? (
              <ConditionalTaggingResultTab key={tenantRemountKey} />
            ) : null}
            {activeTab === "objectToObjectAnalysisResults" ? (
              <ObjectAnalysisTab key={tenantRemountKey} />
            ) : null}
            {activeTab === "implementationRecords" ? (
              <ImplementationRecordTab key={tenantRemountKey} />
            ) : null}
            {activeTab === "triageResult" ? (
              <TriageResultTab key={tenantRemountKey} />
            ) : null}
          </>
        )}
      </Box>
    </Box>
  );
}
